import React, { useEffect, useState } from 'react';
import { assetsAPI, liabilitiesAPI } from '../services/api';

interface Asset {
  id: number;
  category: string;
  name: string;
  value: number;
  currency: string;
  notes?: string;
}

interface Liability {
  id: number;
  category: string;
  name: string;
  balance: number;
  interest_rate?: number;
  minimum_payment?: number;
  notes?: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
  }).format(value);
};

const NetWorth: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'assets' | 'liabilities'>('assets');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [loading, setLoading] = useState(true);

  // Asset modal state
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [assetFormData, setAssetFormData] = useState({
    category: '',
    name: '',
    value: '',
    notes: '',
  });

  // Liability modal state
  const [showLiabilityModal, setShowLiabilityModal] = useState(false);
  const [editingLiability, setEditingLiability] = useState<Liability | null>(null);
  const [liabilityFormData, setLiabilityFormData] = useState({
    category: '',
    name: '',
    balance: '',
    interest_rate: '',
    minimum_payment: '',
    notes: '',
  });

  const assetCategories = ['Cash', 'Registered', 'Non-Registered', 'Real Estate', 'Other'];
  const liabilityCategories = ['Mortgage', 'Auto Loan', 'Credit Card', 'Student Loan', 'Line of Credit', 'Other'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assetsRes, liabilitiesRes] = await Promise.all([
        assetsAPI.getAll(),
        liabilitiesAPI.getAll()
      ]);
      setAssets(assetsRes.data);
      setLiabilities(liabilitiesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Asset handlers
  const handleAssetSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        category: assetFormData.category,
        name: assetFormData.name,
        value: parseFloat(assetFormData.value),
        notes: assetFormData.notes,
      };

      if (editingAsset) {
        await assetsAPI.update(editingAsset.id, data);
      } else {
        await assetsAPI.create(data);
      }

      setShowAssetModal(false);
      setEditingAsset(null);
      setAssetFormData({ category: '', name: '', value: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save asset:', error);
    }
  };

  const handleEditAsset = (asset: Asset) => {
    setEditingAsset(asset);
    setAssetFormData({
      category: asset.category,
      name: asset.name,
      value: asset.value.toString(),
      notes: asset.notes || '',
    });
    setShowAssetModal(true);
  };

  const handleDeleteAsset = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await assetsAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Failed to delete asset:', error);
      }
    }
  };

  // Liability handlers
  const handleLiabilitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const data = {
        category: liabilityFormData.category,
        name: liabilityFormData.name,
        balance: parseFloat(liabilityFormData.balance),
        interest_rate: liabilityFormData.interest_rate ? parseFloat(liabilityFormData.interest_rate) : null,
        minimum_payment: liabilityFormData.minimum_payment ? parseFloat(liabilityFormData.minimum_payment) : null,
        notes: liabilityFormData.notes,
      };

      if (editingLiability) {
        await liabilitiesAPI.update(editingLiability.id, data);
      } else {
        await liabilitiesAPI.create(data);
      }

      setShowLiabilityModal(false);
      setEditingLiability(null);
      setLiabilityFormData({ category: '', name: '', balance: '', interest_rate: '', minimum_payment: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to save liability:', error);
    }
  };

  const handleEditLiability = (liability: Liability) => {
    setEditingLiability(liability);
    setLiabilityFormData({
      category: liability.category,
      name: liability.name,
      balance: liability.balance.toString(),
      interest_rate: liability.interest_rate?.toString() || '',
      minimum_payment: liability.minimum_payment?.toString() || '',
      notes: liability.notes || '',
    });
    setShowLiabilityModal(true);
  };

  const handleDeleteLiability = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this liability?')) {
      try {
        await liabilitiesAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Failed to delete liability:', error);
      }
    }
  };

  const totalAssets = assets.reduce((sum, a) => sum + a.value, 0);
  const totalLiabilities = liabilities.reduce((sum, l) => sum + l.balance, 0);
  const netWorth = totalAssets - totalLiabilities;

  const groupedAssets = assets.reduce((acc, asset) => {
    if (!acc[asset.category]) acc[asset.category] = [];
    acc[asset.category].push(asset);
    return acc;
  }, {} as Record<string, Asset[]>);

  const groupedLiabilities = liabilities.reduce((acc, liability) => {
    if (!acc[liability.category]) acc[liability.category] = [];
    acc[liability.category].push(liability);
    return acc;
  }, {} as Record<string, Liability[]>);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {/* Header with Net Worth */}
      <div className="page-header" style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
        <p className="page-subtitle" style={{ marginBottom: 'var(--space-1)' }}>Net Worth</p>
        <h1 className="page-title" style={{
          fontSize: '2.5rem',
          color: netWorth >= 0 ? 'var(--success)' : 'var(--danger)',
          marginBottom: 'var(--space-3)'
        }}>
          {formatCurrency(netWorth)}
        </h1>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 'var(--space-6)', fontSize: '0.875rem' }}>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>Assets: </span>
            <span style={{ color: 'var(--success)', fontWeight: 600 }}>{formatCurrency(totalAssets)}</span>
          </div>
          <div>
            <span style={{ color: 'var(--text-secondary)' }}>Liabilities: </span>
            <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{formatCurrency(totalLiabilities)}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'assets' ? 'active' : ''}`}
          onClick={() => setActiveTab('assets')}
        >
          Assets ({assets.length})
        </button>
        <button
          className={`tab ${activeTab === 'liabilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('liabilities')}
        >
          Liabilities ({liabilities.length})
        </button>
      </div>

      {/* Assets Tab */}
      {activeTab === 'assets' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <button className="btn btn-primary" onClick={() => setShowAssetModal(true)}>
              + Add Asset
            </button>
          </div>

          {Object.entries(groupedAssets).map(([category, categoryAssets]) => (
            <div key={category} className="card" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="card-header">
                <h3 className="card-title">{category}</h3>
                <span style={{ color: 'var(--success)', fontWeight: 600 }}>
                  {formatCurrency(categoryAssets.reduce((sum, a) => sum + a.value, 0))}
                </span>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ textAlign: 'right' }}>Value</th>
                      <th className="desktop-only">Notes</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryAssets.map((asset) => (
                      <tr key={asset.id}>
                        <td>{asset.name}</td>
                        <td style={{ textAlign: 'right', fontWeight: 500 }}>{formatCurrency(asset.value)}</td>
                        <td className="desktop-only" style={{ color: 'var(--text-secondary)' }}>{asset.notes}</td>
                        <td>
                          <div className="table-actions">
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEditAsset(asset)}>
                              Edit
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAsset(asset.id)}>
                              Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {assets.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
              <p style={{ color: 'var(--text-secondary)' }}>No assets yet. Add your first asset to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Liabilities Tab */}
      {activeTab === 'liabilities' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
            <button className="btn btn-primary" onClick={() => setShowLiabilityModal(true)}>
              + Add Liability
            </button>
          </div>

          {Object.entries(groupedLiabilities).map(([category, categoryLiabilities]) => (
            <div key={category} className="card" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="card-header">
                <h3 className="card-title">{category}</h3>
                <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                  {formatCurrency(categoryLiabilities.reduce((sum, l) => sum + l.balance, 0))}
                </span>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                      <th className="desktop-only" style={{ textAlign: 'right' }}>Rate</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryLiabilities.map((liability) => (
                      <tr key={liability.id}>
                        <td>{liability.name}</td>
                        <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--danger)' }}>
                          {formatCurrency(liability.balance)}
                        </td>
                        <td className="desktop-only" style={{ textAlign: 'right' }}>
                          {liability.interest_rate ? `${liability.interest_rate}%` : '-'}
                        </td>
                        <td>
                          <div className="table-actions">
                            <button className="btn btn-secondary btn-sm" onClick={() => handleEditLiability(liability)}>
                              Edit
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteLiability(liability.id)}>
                              Del
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {liabilities.length === 0 && (
            <div className="card" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
              <p style={{ color: 'var(--text-secondary)' }}>No liabilities. Great job staying debt-free!</p>
            </div>
          )}
        </div>
      )}

      {/* Asset Modal */}
      {showAssetModal && (
        <div className="modal-overlay" onClick={() => setShowAssetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingAsset ? 'Edit Asset' : 'Add Asset'}</h3>
              <button className="modal-close" onClick={() => setShowAssetModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleAssetSubmit}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-input"
                  value={assetFormData.category}
                  onChange={(e) => setAssetFormData({ ...assetFormData, category: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {assetCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={assetFormData.name}
                  onChange={(e) => setAssetFormData({ ...assetFormData, name: e.target.value })}
                  placeholder="e.g., Savings Account"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Value ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={assetFormData.value}
                  onChange={(e) => setAssetFormData({ ...assetFormData, value: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea
                  className="form-input"
                  value={assetFormData.notes}
                  onChange={(e) => setAssetFormData({ ...assetFormData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssetModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingAsset ? 'Update' : 'Add'} Asset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Liability Modal */}
      {showLiabilityModal && (
        <div className="modal-overlay" onClick={() => setShowLiabilityModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingLiability ? 'Edit Liability' : 'Add Liability'}</h3>
              <button className="modal-close" onClick={() => setShowLiabilityModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleLiabilitySubmit}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-input"
                  value={liabilityFormData.category}
                  onChange={(e) => setLiabilityFormData({ ...liabilityFormData, category: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {liabilityCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={liabilityFormData.name}
                  onChange={(e) => setLiabilityFormData({ ...liabilityFormData, name: e.target.value })}
                  placeholder="e.g., TD Mortgage"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Balance ($)</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-input"
                  value={liabilityFormData.balance}
                  onChange={(e) => setLiabilityFormData({ ...liabilityFormData, balance: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Interest Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={liabilityFormData.interest_rate}
                    onChange={(e) => setLiabilityFormData({ ...liabilityFormData, interest_rate: e.target.value })}
                    placeholder="5.25"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Min Payment ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-input"
                    value={liabilityFormData.minimum_payment}
                    onChange={(e) => setLiabilityFormData({ ...liabilityFormData, minimum_payment: e.target.value })}
                    placeholder="500.00"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes (optional)</label>
                <textarea
                  className="form-input"
                  value={liabilityFormData.notes}
                  onChange={(e) => setLiabilityFormData({ ...liabilityFormData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={3}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowLiabilityModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingLiability ? 'Update' : 'Add'} Liability
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NetWorth;
